import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatIDR } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
    Bot,
    X,
    Send,
    Camera,
    Loader2,
    Trash2,
    Sparkles,
    CheckCircle2,
    ImageIcon,
} from "lucide-react";

// Perkecil foto ke maks 1600px & kompres JPEG agar payload ringan
function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const max = 1600;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(img.src);
            resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = () => reject(new Error("File gambar tidak valid"));
        img.src = URL.createObjectURL(file);
    });
}

function PackageCard({ msg, savingId, onSave, onOpen }) {
    const draft = msg.package_draft;
    const saved = !!msg.saved_package_id;
    return (
        <div
            className="mt-2 rounded-lg border border-[#C4DAD2] bg-white overflow-hidden"
            data-testid="ai-chat-pkg-card"
        >
            <div className="bg-[#E9EFEA] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-[#245B4B] flex items-center gap-1">
                    <Sparkles size={11} /> Draf Paket
                </div>
                <div className="font-heading font-semibold text-sm text-[#1A362D]">
                    {draft.package_name}
                </div>
            </div>
            <ul className="px-3 py-2 max-h-40 overflow-y-auto divide-y divide-[#F0F4F1]">
                {draft.items.map((it, i) => (
                    <li key={i} className="py-1 flex items-start justify-between gap-2 text-xs">
                        <span className="text-[#4A5E57]">
                            {it.name}
                            {!it.matched && (
                                <span className="ml-1 rounded bg-amber-100 text-amber-700 px-1 py-0.5 text-[10px]">
                                    baru
                                </span>
                            )}
                        </span>
                        <span className="text-[#245B4B] whitespace-nowrap">
                            {formatIDR(
                                it.matched
                                    ? it.price_paket || it.price
                                    : it.price_estimate
                            )}
                        </span>
                    </li>
                ))}
            </ul>
            <div className="px-3 py-2 border-t border-[#F0F4F1] flex items-center justify-between text-xs">
                <span className="text-[#6A9C89]">{draft.items.length} pemeriksaan</span>
                <span className="font-semibold text-[#1A362D]">
                    {formatIDR(draft.total_estimate)}
                </span>
            </div>
            <div className="px-3 pb-3">
                {saved ? (
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 text-xs text-[#245B4B]">
                            <CheckCircle2 size={14} /> Tersimpan
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onOpen(msg.saved_package_id)}
                            data-testid="ai-chat-open-calc-btn"
                            className="flex-1 h-8 text-xs border-[#245B4B] text-[#245B4B] hover:bg-[#E9EFEA]"
                        >
                            Buka di Kalkulator
                        </Button>
                    </div>
                ) : (
                    <Button
                        size="sm"
                        onClick={() => onSave(msg)}
                        disabled={savingId === msg.id}
                        data-testid="ai-chat-save-pkg-btn"
                        className="w-full h-8 text-xs bg-[#245B4B] hover:bg-[#1A4236] text-white"
                    >
                        {savingId === msg.id ? (
                            <>
                                <Loader2 size={13} className="mr-1 animate-spin" /> Menyimpan...
                            </>
                        ) : (
                            "Simpan ke Paket Tersimpan"
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
}

export function AiChatWidget() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [image, setImage] = useState("");
    const [sending, setSending] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (open && !loaded) {
            api.get("/ai/chat/history")
                .then((res) => setMessages(res.data))
                .catch(() => {})
                .finally(() => setLoaded(true));
        }
    }, [open, loaded]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, sending, open]);

    async function handleFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        try {
            setImage(await fileToBase64(file));
        } catch (err) {
            toast.error(err.message);
        }
    }

    async function send() {
        const text = input.trim();
        if ((!text && !image) || sending) return;
        const img = image;
        setInput("");
        setImage("");
        setSending(true);
        setMessages((m) => [
            ...m,
            { id: "temp", role: "user", content: text, has_image: !!img },
        ]);
        try {
            const res = await api.post(
                "/ai/chat",
                { message: text, image_base64: img || null },
                { timeout: 180000 }
            );
            setMessages((m) => [
                ...m.filter((x) => x.id !== "temp"),
                res.data.user_message,
                res.data.assistant_message,
            ]);
        } catch (e) {
            toast.error(formatApiError(e));
            setMessages((m) => m.filter((x) => x.id !== "temp"));
            setInput(text);
            if (img) setImage(img);
        } finally {
            setSending(false);
        }
    }

    async function savePackage(msg) {
        setSavingId(msg.id);
        try {
            const res = await api.post("/ai/chat/save-package", { message_id: msg.id });
            toast.success(`Paket "${res.data.package_name}" tersimpan`);
            if (res.data.created?.length > 0) {
                toast.info(
                    `${res.data.created.length} pemeriksaan baru dibuat di master (unit cost kosong)`
                );
            }
            setMessages((m) =>
                m.map((x) =>
                    x.id === msg.id ? { ...x, saved_package_id: res.data.package_id } : x
                )
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingId(null);
        }
    }

    async function clearChat() {
        if (!window.confirm("Hapus seluruh riwayat percakapan?")) return;
        try {
            await api.delete("/ai/chat/history");
            setMessages([]);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    function openCalculator(pkgId) {
        setOpen(false);
        navigate(`/mcu/${pkgId}`);
    }

    return (
        <>
            {open && (
                <div
                    data-testid="ai-chat-panel"
                    className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[390px] h-[min(600px,calc(100vh-8rem))] bg-white rounded-xl shadow-2xl border border-[#D1DCD8] flex flex-col overflow-hidden animate-fade-in-up"
                >
                    <div className="bg-[#245B4B] text-white px-4 py-3 flex items-center gap-2 shrink-0">
                        <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                            <Bot size={17} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-heading font-semibold text-sm leading-tight">
                                Asisten AI
                            </div>
                            <div className="text-[11px] text-white/70">
                                Tanya paket MCU atau kirim foto brosur
                            </div>
                        </div>
                        <button
                            onClick={clearChat}
                            title="Hapus riwayat"
                            data-testid="ai-chat-clear-btn"
                            className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
                        >
                            <Trash2 size={15} />
                        </button>
                        <button
                            onClick={() => setOpen(false)}
                            title="Tutup"
                            data-testid="ai-chat-close-btn"
                            className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F7F9F6]">
                        {messages.length === 0 && !sending && (
                            <div className="text-center text-xs text-[#6A9C89] mt-8 px-4 space-y-2">
                                <Bot size={28} className="mx-auto text-[#C4DAD2]" />
                                <p>
                                    Halo! Saya bisa menyusun paket MCU sesuai kebutuhan, atau
                                    membaca foto brosur paket. Contoh:
                                </p>
                                <p className="italic">
                                    "Buatkan paket MCU karyawan pria usia 40 tahun ke atas"
                                </p>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div
                                key={m.id || i}
                                data-testid={`ai-chat-message-${i}`}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-[#245B4B] text-white rounded-br-sm"
                                            : "bg-white border border-[#E2E9E5] text-[#1A362D] rounded-bl-sm"
                                    }`}
                                >
                                    {m.has_image && (
                                        <div
                                            className={`flex items-center gap-1 text-xs mb-1 ${
                                                m.role === "user" ? "text-white/80" : "text-[#6A9C89]"
                                            }`}
                                        >
                                            <ImageIcon size={13} /> Foto brosur
                                        </div>
                                    )}
                                    {m.content}
                                    {m.package_draft && (
                                        <PackageCard
                                            msg={m}
                                            savingId={savingId}
                                            onSave={savePackage}
                                            onOpen={openCalculator}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start" data-testid="ai-chat-typing">
                                <div className="bg-white border border-[#E2E9E5] rounded-xl rounded-bl-sm px-3 py-2 flex items-center gap-2 text-sm text-[#6A9C89]">
                                    <Loader2 size={14} className="animate-spin" />
                                    AI sedang berpikir...
                                </div>
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    <div className="border-t border-[#E2E9E5] p-2.5 bg-white shrink-0">
                        {image && (
                            <div className="mb-2 flex items-center gap-2 bg-[#E9EFEA] rounded-md p-1.5">
                                <img
                                    src={image}
                                    alt="Lampiran brosur"
                                    data-testid="ai-chat-image-preview"
                                    className="h-10 w-10 rounded object-cover"
                                />
                                <span className="flex-1 text-xs text-[#4A5E57]">
                                    Foto brosur siap dikirim
                                </span>
                                <button
                                    onClick={() => setImage("")}
                                    data-testid="ai-chat-remove-image-btn"
                                    className="p-1 rounded hover:bg-white text-[#4A5E57]"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                        <div className="flex items-end gap-1.5">
                            <button
                                onClick={() =>
                                    document.getElementById("ai-chat-file-input").click()
                                }
                                disabled={sending}
                                title="Kirim foto brosur"
                                data-testid="ai-chat-attach-btn"
                                className="p-2 rounded-md text-[#245B4B] hover:bg-[#E9EFEA] transition-colors disabled:opacity-50"
                            >
                                <Camera size={19} />
                            </button>
                            <input
                                id="ai-chat-file-input"
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                capture="environment"
                                className="hidden"
                                data-testid="ai-chat-file-input"
                                onChange={handleFile}
                            />
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                                rows={1}
                                placeholder="Tulis pesan..."
                                data-testid="ai-chat-input"
                                className="flex-1 resize-none rounded-md border border-[#D1DCD8] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6A9C89] max-h-24"
                            />
                            <button
                                onClick={send}
                                disabled={sending || (!input.trim() && !image)}
                                data-testid="ai-chat-send-btn"
                                className="p-2 rounded-md bg-[#245B4B] text-white hover:bg-[#1A4236] transition-colors disabled:opacity-40"
                            >
                                <Send size={17} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <button
                onClick={() => setOpen((o) => !o)}
                data-testid="ai-chat-fab"
                title="Asisten AI"
                className="fixed bottom-6 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-[#245B4B] hover:bg-[#1A4236] text-white shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
            >
                {open ? <X size={22} /> : <Bot size={24} />}
            </button>
        </>
    );
}
